import React from 'react';
import { useIntl } from 'react-intl';
import { localize } from '@saviynt/common';
import PropTypes from 'prop-types';

import Link from '../../../Link/Link';

import './MenuMultiFooter.css';

const msgs = {
  neo: {
    common: {
      selectAll: {
        id: 'neo.common.selectAll',
        defaultMessage: 'Select All',
      },
      clear: {
        id: 'neo.common.clear',
        defaultMessage: 'Clear',
      },
    },
  },
};

function MenuMultiFooter({
  onSelectAll,
  onClear,
  isClearDisabled,
  isSelectAllDisabled,
  footerLink1Text,
  onFooterLink1Click,
  footerLink2Text,
  onFooterLink2Click,
  className,
  tabIndex,
  dataTestId,
}) {
  const intl = useIntl();
  const SELECT_ALL_TEXT = localize(intl, msgs.neo.common.selectAll);
  const CLEAR_TEXT = localize(intl, msgs.neo.common.clear);

  return (
    <div className={`MenuMultiFooter ${className}`} data-testid={dataTestId}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        text={footerLink1Text || SELECT_ALL_TEXT}
        kind='button'
        onClick={onFooterLink1Click || onSelectAll}
        isDisabled={isSelectAllDisabled}
        className='MenuMultiFooter-selectAll'
        tabIndex={tabIndex}
        size='small'
      />

      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        text={footerLink2Text || CLEAR_TEXT}
        kind='button'
        onClick={onFooterLink2Click || onClear}
        isDisabled={isClearDisabled}
        className='MenuMultiFooter-clear'
        size='small'
        tabIndex={tabIndex}
      />
    </div>
  );
}

const isFunction = (prop) => typeof prop === 'function';

const onSelectAllOrOnFooterLink1ClickPropType = (
  props,
  propName,
  componentName
) => {
  const { onSelectAll, onFooterLink1Click } = props;

  if (!onSelectAll && !onFooterLink1Click) {
    return new Error(
      `One of props 'onSelectAll' or 'onFooterLink1Click' was not passed to '${componentName}'.`
    );
  }

  if (onSelectAll && !isFunction(onSelectAll)) {
    return new Error(
      `Invalid prop 'onSelectAll' passed to '${componentName}'. Expected a function.`
    );
  }

  if (onFooterLink1Click && !isFunction(onFooterLink1Click)) {
    return new Error(
      `Invalid prop 'onFooterLink1Click' passed to '${componentName}'. Expected a function.`
    );
  }

  return null;
};

const onClearOrOnFooterLink2ClickPropType = (
  props,
  propName,
  componentName
) => {
  const { onClear, onFooterLink2Click } = props;

  if (!onClear && onFooterLink2Click) {
    return new Error(
      `One of props 'onClear' or 'onFooterLink2Click' was not passed to '${componentName}'.`
    );
  }

  if (onClear && !isFunction(onClear)) {
    return new Error(
      `Invalid prop 'onClear' passed to '${componentName}'. Expected a function.`
    );
  }

  if (onFooterLink2Click && !isFunction(onFooterLink2Click)) {
    return new Error(
      `Invalid prop 'onFooterLink2Click' passed to '${componentName}'. Expected a function.`
    );
  }

  return null;
};

MenuMultiFooter.propTypes = {
  onSelectAll: onSelectAllOrOnFooterLink1ClickPropType,
  onClear: onClearOrOnFooterLink2ClickPropType,
  isClearDisabled: PropTypes.bool,
  isSelectAllDisabled: PropTypes.bool,
  footerLink1Text: PropTypes.string,
  footerLink2Text: PropTypes.string,
  onFooterLink1Click: onSelectAllOrOnFooterLink1ClickPropType,
  onFooterLink2Click: onClearOrOnFooterLink2ClickPropType,
  className: PropTypes.string,
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dataTestId: PropTypes.string,
};
MenuMultiFooter.defaultProps = {
  onSelectAll: null,
  onClear: null,
  isClearDisabled: true,
  isSelectAllDisabled: true,
  footerLink1Text: '',
  onFooterLink1Click: null,
  footerLink2Text: '',
  onFooterLink2Click: null,
  className: null,
  tabIndex: 0,
  dataTestId: null,
};

export default MenuMultiFooter;
